import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useApolloClient, useQuery } from '@apollo/client';

import { useFillerVideosQuery, useMyDataQuery, useMyFontsQuery } from '@/generated/graphql';
import { buildStudioRenderInput, type StudioRenderKit } from '@/lib/freecut-studio/studioRenderInput';
import { fillerCatalogUrls } from '@/lib/layouts/filterFillerVideos';
import { useHasFeature } from '@/lib/useHasFeature';
import {
  MY_CLIP_RENDER_DATA,
  type MyClipRenderDataResult,
  type MyClipRenderDataVariables,
} from '@/queries/clipStudio';
import { GET_MY_BRAND_KITS, GET_MY_LIBRARY, GET_MY_PROJECT } from '@/queries/queries';

export interface StudioClip {
  id: string;
  name?: string | null;
  duration_seconds?: number | null;
  data?: unknown;
  is_library_clip?: boolean | null;
  project_id?: string | null;
  library_id?: string | null;
}

/** `myProject` / `myLibrary` `source_status` (absent on older backends: ready). */
export type StudioSourceStatus = 'ready' | 'preparing' | 'failed' | 'expired';

export interface ClipStudioRenderData {
  /** Everything the render input is built from has loaded. */
  ready: boolean;
  sourceStatus: StudioSourceStatus;
  /** The clip's render data as it would export now (null when the clip is not found). */
  load: () => Promise<Record<string, unknown> | null>;
}

/**
 * What FreeCut Studio imports: the clip's render data, asked of the backend with the export
 * input the editor would send (see `buildStudioRenderInput`), so the studio opens any clip as
 * it would export, exported before or not.
 */
export function useClipStudioRenderData(clip: StudioClip | null | undefined): ClipStudioRenderData {
  const { i18n } = useTranslation();
  const client = useApolloClient();
  const libraryId = clip?.is_library_clip ? clip.library_id : null;
  const projectId = clip && !clip.is_library_clip ? clip.project_id : null;
  const library = useQuery(GET_MY_LIBRARY, { variables: { id: libraryId }, skip: !libraryId });
  const project = useQuery(GET_MY_PROJECT, { variables: { id: projectId }, skip: !projectId });
  const brandKits = useQuery(GET_MY_BRAND_KITS);
  const fonts = useMyFontsQuery();
  const fillers = useFillerVideosQuery();
  const myData = useMyDataQuery();
  const hasProFeatures = useHasFeature('pro_features');

  const source = libraryId ? library.data?.myLibrary : project.data?.myProject;
  const sourceSettled = libraryId ? !library.loading : !!projectId && !project.loading;
  const ready = !!clip && sourceSettled && !!source && !brandKits.loading && !fonts.loading
    && !fillers.loading && !myData.loading;
  const status = source?.source_status;
  const sourceStatus: StudioSourceStatus = status === 'preparing' || status === 'failed' || status === 'expired'
    ? status
    : 'ready';

  const load = useCallback(async () => {
    if (!clip || !source) return null;
    const input = buildStudioRenderInput({
      source,
      clip,
      brandKits: (brandKits.data?.myBrandKits ?? []) as StudioRenderKit[],
      hasProFeatures,
      fonts: fonts.data?.myFonts,
      user: myData.data?.myData,
      fillerPool: fillerCatalogUrls(fillers.data?.fillerVideos ?? [], i18n.language),
    });
    const { data } = await client.query<MyClipRenderDataResult, MyClipRenderDataVariables>({
      query: MY_CLIP_RENDER_DATA,
      variables: { id: clip.id, input },
      fetchPolicy: 'no-cache',
    });
    return data?.myClipRenderData ?? null;
  }, [
    clip,
    source,
    brandKits.data,
    hasProFeatures,
    fonts.data,
    myData.data,
    fillers.data,
    i18n.language,
    client,
  ]);

  return { ready, sourceStatus, load };
}
